import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Form from '@cloudscape-design/components/form'
import FormField from '@cloudscape-design/components/form-field'
import Input from '@cloudscape-design/components/input'
import Modal from '@cloudscape-design/components/modal'
import SpaceBetween from '@cloudscape-design/components/space-between'
import { enterMissingEmailsSchema, type EnterMissingEmailsFormData } from '@/lib/schemas'
import type { Provider } from '@/lib/types'

interface EnterMissingEmailsModalProps {
  providers: Provider[]
  visible: boolean
  onDismiss: () => void
  onApply: (emails: Record<string, string>) => void
}

export default function EnterMissingEmailsModal({
  providers,
  visible,
  onDismiss,
  onApply,
}: EnterMissingEmailsModalProps) {
  const { control, handleSubmit, formState: { errors } } = useForm<EnterMissingEmailsFormData>({
    resolver: zodResolver(enterMissingEmailsSchema),
    defaultValues: { rows: providers.map((p) => ({ id: p._id, email: '' })) },
  })

  const { fields } = useFieldArray({ control, name: 'rows' })

  function onSubmit(data: EnterMissingEmailsFormData) {
    const emails: Record<string, string> = {}
    for (const row of data.rows) {
      if (row.email) emails[row.id] = row.email
    }
    onApply(emails)
  }

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Enter missing email addresses"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>Cancel</Button>
            <Button variant="primary" onClick={() => { void handleSubmit(onSubmit)() }}>Apply</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form>
        <SpaceBetween size="m">
          <Box variant="p" color="text-body-secondary">
            Enter email addresses for the providers below. Leave a field blank to skip that provider.
          </Box>
          {fields.map((field, index) => {
            const provider = providers[index]
            return (
              <Controller
                key={field.id}
                control={control}
                name={`rows.${index}.email`}
                render={({ field: f }) => (
                  <FormField
                    label={provider?.provider_name ?? field.id}
                    errorText={errors.rows?.[index]?.email?.message}
                  >
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      value={f.value}
                      onChange={({ detail }) => f.onChange(detail.value)}
                    />
                  </FormField>
                )}
              />
            )
          })}
        </SpaceBetween>
      </Form>
    </Modal>
  )
}
